import { type Nullable } from "../types/types";

import { toLookup } from "./helpers";

let uuid = 0;

export interface ChapterPayload {
  book_id: number;
  chapter_number: number;
  has_related_items: boolean;
  id: number;
  related_items?: RelatedResource[];
  [unknown: string]: unknown;
}

export type ContentOptions = {
  skipChapters?: boolean;
};

export class Content {
  readonly items: ContentItemUnion[] = [];

  readonly key = `bible-content-${uuid++}`;

  readonly query: string;

  readonly translation: number;

  readonly verses = new Map<number, ContentVerse>();

  get string() {
    return this.items
      .flatMap((item) => {
        if (item instanceof ContentTable) {
          return item.rows
            .flatMap((row) =>
              row.cells
                .flatMap((cell) =>
                  cell.items.flatMap((item) => item.content).join(""),
                )
                .join(" | "),
            )
            .join(" --- ");
        }
        if (item instanceof ContentParagraph) {
          return item.items.flatMap((item) => {
            if (item instanceof ContentLink) {
              return item.items.flatMap((item) => item.content);
            }
            if (item instanceof ContentItem) {
              return [item.content];
            }
            return [];
          });
        }
        return [];
      })
      .join("");
  }

  constructor(queryPayload: QueryPayload, options: ContentOptions = {}) {
    const items: ContentItemUnion[] = [];
    const payloadItems = queryPayload.items /* c8 ignore next */ ?? [];
    const verses = toLookup(
      queryPayload.verses /* c8 ignore next */ ?? [],
      (verse) => verse.id,
    );
    let chapterId: number | undefined;
    let currentLink: ContentLink | undefined;
    let currentTable: ContentTable | undefined;
    let currentNonCharItem: ContentItem | undefined;
    let currentParagraph: ContentParagraph | undefined;
    let verseId: number | undefined;
    const commitCurrentParagraph = () => {
      if (!currentParagraph) return;
      if (currentParagraph.items.length) {
        items.push(currentParagraph);
      }
      currentParagraph = undefined;
    };
    const appendImages = () => {
      if (!verseId) return;
      const versePayload = verses.get(verseId);
      if (versePayload) {
        if (versePayload.images_after_verse) {
          for (const imagePayload of versePayload.images_after_verse) {
            currentParagraph?.items.push(new ContentVerseImage(imagePayload));
          }
          versePayload.images_after_verse = [];
        }
      }
    };
    for (
      let payloadIndex = 0, length = payloadItems.length;
      payloadIndex < length;
      payloadIndex++
    ) {
      const payload = payloadItems[payloadIndex];
      const payloadNext = payloadItems[payloadIndex + 1] as
        | ContentItemPayload
        | undefined;
      let item: ContentItem | undefined = new ContentItem(payload);
      // This is to work around the fact that we lose the paragraph parent due to the way that
      // character styles can be nested.
      // For instance if an item style is "sc,it" the parent_style will be "sc"
      // which is technically correct, but not helpful for checking if an item is a heading.
      if (!item.styles.some((s) => s.isChar)) {
        currentNonCharItem = item;
      }
      if (item.isParagraphStart) {
        commitCurrentParagraph();
      }
      // Chapter
      chapter: if (payload.chapter_id !== chapterId) {
        chapterId = payload.chapter_id;
        if (options.skipChapters === true) break chapter;
        const chapter = new ContentChapter({
          book_id: queryPayload.book_id,
          book_name: queryPayload.book_name,
          chapter_number: payload.chapter_number,
          id: payload.chapter_id,
          reference: payload.string,
        });
        items.push(chapter);
      }
      // Verse
      let verse: ContentVerse | undefined;
      if (payload.verse_id !== verseId) {
        const versePayload = verses.get(payload.verse_id);
        if (versePayload) {
          verse = new ContentVerse(payload, versePayload, queryPayload.book_id);
          this.verses.set(verse.id, verse);
        }
      }
      // Table
      // Skip paragraphs that immediately proceed a table
      if (item.isParagraphStart && payloadNext?.table) continue;
      if (payload.table) {
        commitCurrentParagraph();
        // Many of our tables have each row split into its own "table" object with one row,
        // so we just merge consecutive tables into a single table
        if (currentTable) {
          currentTable.append(payload.table, verse);
        } else {
          const table = new ContentTable(payload.table, verse);
          items.push(table);
          currentTable = table;
        }
        continue;
      } else {
        currentTable = undefined;
      }
      if (item.shouldBeSkipped) continue;
      // Paragraph
      if (item.isParagraphStart && !currentParagraph) {
        currentParagraph = new ContentParagraph(item);
        continue;
      } else if (!currentParagraph) {
        currentParagraph = new ContentParagraph(item);
      }
      // Pre-verse items (images, verse numbers)
      if (
        verse &&
        // Don't insert images or verse numbers before/in a heading
        item.isHeading === false &&
        currentNonCharItem?.isHeading === false
      ) {
        const versePayload = verses.get(verse.id);
        if (versePayload) {
          if (versePayload.images_before_verse) {
            for (let imagePayload of versePayload.images_before_verse) {
              currentParagraph.items.push(new ContentVerseImage(imagePayload));
            }
          }
          // Verse number
          currentParagraph.items.push(verse);
          verseId = payload.verse_id;
        }
      }
      // Link
      if (payload.links && payload.links.length > 0) {
        // Use the first link as the "id" since there is only one link in practice
        const [{ id }] = payload.links;
        // Track the same link across multiple items
        // This can happen if a link is a phrase and there happens to be formatting in the middle
        if (id == currentLink?.payloads[0].id) {
          currentLink.items.push(item);
        } else {
          currentLink = new ContentLink(payload.links);
          currentLink.items.push(item);
          currentParagraph.items.push(currentLink);
        }
        item = undefined;
      } else {
        currentLink = undefined;
      }
      // Item
      if (item) {
        currentParagraph.items.push(item);
      }
      // Post-verse items (images)
      if (payloadNext && payloadNext.verse_id !== verseId) {
        appendImages();
      }
    }
    appendImages();
    commitCurrentParagraph();
    this.items = items;
    this.query = queryPayload.query;
    this.translation = queryPayload.translation_id;
  }
}

export interface ContentChapterPayload {
  book_id: number;
  book_name: string;
  chapter_number: number;
  id: number;
  reference: string;
}

export class ContentChapter {
  get book() {
    return {
      id: this.#payload.book_id,
      name: this.#payload.book_name,
    };
  }

  get id() {
    return this.#payload.id;
  }

  readonly key = `bible-content-chapter-${uuid++}`;

  get number() {
    return this.#payload.chapter_number;
  }

  #payload: ContentChapterPayload;

  constructor(payload: ContentChapterPayload) {
    this.#payload = payload;
  }

  toString() {
    return `${this.constructor.name} (${this.#payload.reference})`;
  }
}

export interface ContentItemPayload {
  chapter_id: number;
  chapter_number: number;
  content: Nullable<string>;
  links: Nullable<ContentLinkPayload[]>;
  parent_style: Nullable<string>;
  string: string;
  style: Nullable<string>;
  table?: Nullable<ContentTablePayload>;
  type: "paragraph-start" | "table" | "text";
  verse_id: number;
  verse_number: number;
  verse_number_end?: Nullable<number>;
  [unknown: string]: unknown;
}

export type ContentItemUnion = ContentChapter | ContentTable | ContentParagraph;

export class ContentItem {
  get content() {
    return this.#payload.content;
  }

  get isHeading() {
    // This is not 100% reliable if an item has multiple character styles ("sc,it")
    // because the parent_style will be the parent character style ("sc")
    return (
      this.styles?.some((s) => s.isHeading) ||
      this.parentStyles?.some((s) => s.isHeading)
    );
  }

  get isParagraphStart() {
    return this.type === "paragraph-start";
  }

  get isText() {
    return this.type === "text";
  }

  readonly key = `bible-content-item-${uuid++}`;

  get parentStyleValue() {
    return this.#payload.parent_style;
  }

  readonly parentStyles: ContentItemStyle[] = [];

  #payload: ContentItemPayload;

  get reference() {
    return this.#payload.string;
  }

  get shouldBeSkipped() {
    return (
      this.styles?.some((s) => s.isSkipped) ||
      this.parentStyles?.some((s) => s.isSkipped)
    );
  }

  get styleValue() {
    return this.#payload.style;
  }

  readonly styles: ContentItemStyle[] = [];

  get styleAttribute() {
    return this.styles.map((s) => `_${s.value}_`).join(" ");
  }

  get type() {
    return this.#payload.type;
  }

  constructor(payload: ContentItemPayload) {
    this.#payload = payload;
    this.parentStyles = ContentItemStyle.from(payload.parent_style);
    this.styles = ContentItemStyle.from(payload.style);
  }

  toString() {
    return `${this.constructor.name} (${this.reference})`;
  }
}

export class ContentItemStyle {
  get isChar() {
    return this.isCharacter || this.isSpecialCharacter;
  }

  get isCharacter() {
    return ContentItemStyle.characterCases.has(this.value);
  }

  get isIdentification() {
    return ContentItemStyle.identficationCases.has(this.value);
  }

  get isIntro() {
    return ContentItemStyle.introCases.has(this.value);
  }

  get isHeading() {
    return ContentItemStyle.headingCases.has(this.value);
  }

  get isHeadingMain() {
    return this.isHeading && this.value.startsWith("m");
  }

  get isHeadingSection() {
    return this.isHeading && this.value.startsWith("s");
  }

  get isPara() {
    return (
      this.isIdentification ||
      this.isIntro ||
      this.isHeading ||
      this.isParagraph ||
      this.isPoetry
    );
  }

  get isParagraph() {
    return ContentItemStyle.paragraphCases.has(this.value);
  }

  get isPoetry() {
    return ContentItemStyle.poetryCases.has(this.value);
  }

  get isSkipped() {
    return (
      ContentItemStyle.skippedCases.has(this.value) ||
      this.isIdentification ||
      this.isIntro
    );
  }

  get isSpecialCharacter() {
    return ContentItemStyle.specialCharacterCases.has(this.value);
  }

  get isTable() {
    return ContentItemStyle.tableCases.has(this.value);
  }

  get isTableHeader() {
    return this.isTable && this.value.startsWith("th");
  }

  readonly value: string;

  constructor(value: string) {
    this.value = value;
  }

  static from(value: string | undefined | null): ContentItemStyle[] {
    if (!value) return [];
    return value.split(",").map((v) => new ContentItemStyle(v));
  }

  /*static fromFirst(value: string): ContentItemStyle | undefined {
    return new ContentItemStyle(value.split(",")[0]);
  }*/

  private static characterCases = new Set([
    "bd",
    "bdit",
    "em",
    "it",
    "no",
    "sc",
  ]);

  private static identficationCases = new Set([
    "ide",
    "rem",
    "h",
    "toc1",
    "toc2",
    "toc3",
  ]);

  private static introCases = new Set([
    "imt",
    "is",
    "ip",
    "ipi",
    "im",
    "imi",
    "ipq",
    "imq",
    "ipr",
    "iq",
    "ib",
    "ili",
    "iot",
    "io",
    "iex",
    "imte",
    "ie",
  ]);

  private static headingCases = new Set([
    "mt",
    "mt1",
    "mt2",
    "mt3",
    "mte",
    "mte1",
    "mte2",
    "mte3",
    "ms",
    "ms1",
    "ms2",
    "ms3",
    "mr",
    "s",
    "s1",
    "s2",
    "s3",
    "sr",
    "r",
    "d",
    "sp",
  ]);

  private static paragraphCases = new Set([
    "p",
    "m",
    "pmo",
    "pm",
    "pmr",
    "pi",
    "pi1",
    "pi2",
    "pi3",
    "mi",
    "cls",
    "li",
    "li1",
    "li2",
    "li3",
    "li4",
    "pc",
    "pr",
    "ph",
    "ph1",
    "ph2",
    "ph3",
    "lit",
  ]);

  private static poetryCases = new Set([
    "q",
    "q1",
    "q2",
    "q3",
    "qr",
    "qc",
    "qa",
    "qac",
    "qm",
    "qm1",
    "qm2",
    "qm3",
    "b",
  ]);

  private static skippedCases = new Set([
    "h",
    "ib",
    "ide",
    "ie",
    "ili1",
    "ili2",
    "ili3",
    "iex",
    "im",
    "imi",
    "imq",
    "imt",
    "imt1",
    "imt2",
    "imt3",
    "io1",
    "io2",
    "io3",
    "ior",
    "iot",
    "ip",
    "ipi",
    "ipr",
    "ipq",
    "iqt",
    "is",
    "is1",
    "is2",
    "is3",
    "ms",
    "ms1",
    "ms2",
    "ms3",
    "mt",
    "mt1",
    "mt2",
    "mt3",
    "toc1",
    "toc2",
    "toc3",
    "rem",
    // Undocumented
    "xt", // Ezra 1 - NIrV
  ]);

  private static specialCharacterCases = new Set([
    "ior",
    "iqt",
    "add",
    "bk",
    "dc",
    "k",
    "nd",
    "ord",
    "pn",
    /*"qac",*/ "qs",
    "qt",
    "rq",
    "sig",
    "sls",
    "tl",
    "wj",
  ]);

  private static tableCases = new Set([
    "th1",
    "th2",
    "th3",
    "thr1",
    "thr2",
    "thr3",
    "tc1",
    "tc2",
    "tc3",
    "tcr1",
    "tcr2",
    "tcr3",
  ]);
}

export interface ContentLinkPayload {
  first_movement_readung?: MovementReading;
  first_movement_study?: StudyPayload;
  id: number;
  images?: ImagesPayload;
  scripture_references?: string[];
  study_id: number;
  title: string;
  [unknown: string]: unknown;
}

export class ContentLink {
  get id() {
    return this.payload.id;
  }

  items: ContentItem[] = [];

  readonly key = `bible-content-link-${uuid++}`;

  readonly payload: ContentLinkPayload;

  readonly payloads: ContentLinkPayload[];

  constructor(payloads: ContentLinkPayload[]) {
    this.payload = payloads[0];
    this.payloads = payloads;
  }

  toString() {
    return `${this.constructor.name}`;
  }
}

export type ContentTablePayload = ContentTableRowPayload[];

export interface ContentTableCellPayload {
  align: Nullable<string>;
  items: Nullable<ContentTableCellItemPayload[]>;
}

export interface ContentTableCellItemPayload {
  content: Nullable<string>;
  style: Nullable<string>;
}

export type ContentTableRowPayload = ContentTableCellPayload[];

export class ContentTable {
  readonly hasHeaders: boolean;

  readonly key = `bible-content-table-${uuid++}`;

  readonly rows: ContentTableRow[];

  #rowVerses = new Set<number>();

  constructor(payload: ContentTablePayload, verse?: ContentVerse) {
    this.rows = payload.map(
      (row) => new ContentTableRow(row, this.#getRowVerse(verse)),
    );
    this.hasHeaders = this.rows[0]?.cells[0]?.isHeader;
  }

  append(payload: ContentTablePayload, verse?: ContentVerse) {
    this.rows.push(
      ...payload.map(
        (row) => new ContentTableRow(row, this.#getRowVerse(verse)),
      ),
    );
  }

  toString() {
    return `${this.constructor.name}`;
  }

  #getRowVerse(verse?: ContentVerse): ContentVerse | undefined {
    if (!verse) return undefined;
    if (this.#rowVerses.has(verse.id)) return undefined;
    this.#rowVerses.add(verse.id);
    return verse;
  }
}

class ContentTableCell {
  get align() {
    return this.#payload.align;
  }

  readonly items: ContentTableCellItem[];

  readonly isHeader: boolean;

  readonly key = `bible-content-table-cell-${uuid++}`;

  #payload: ContentTableCellPayload;

  constructor(payload: ContentTableCellPayload) {
    this.items =
      payload.items?.map(
        (i) => new ContentTableCellItem(i),
      ) /* c8 ignore next */ ?? [];
    this.isHeader = this.items.some((i) => i.isHeader);
    this.#payload = payload;
  }
}

class ContentTableCellItem {
  get content() {
    return this.#payload.content;
  }

  get isHeader() {
    return this.styles.some((s) => s.isTableHeader);
  }

  readonly key = `bible-content-table-cell-item-${uuid++}`;

  #payload: ContentTableCellItemPayload;

  get styleAttribute() {
    return this.styles.map((s) => `_${s.value}_`).join(" ");
  }

  private styles: ContentItemStyle[] = [];

  constructor(payload: ContentTableCellItemPayload) {
    this.#payload = payload;
    this.styles = ContentItemStyle.from(payload.style);
  }
}

class ContentTableRow {
  readonly cells: ContentTableCell[];

  readonly key = `bible-content-table-row-${uuid++}`;

  readonly verse?: ContentVerse;

  constructor(payload: ContentTableRowPayload, verse?: ContentVerse) {
    this.cells = payload.map((c) => new ContentTableCell(c));
    this.verse = verse;
  }
}

export type ContentParagraphItemUnion =
  | ContentItem
  | ContentLink
  | ContentVerse
  | ContentVerseImage;

export class ContentParagraph {
  readonly item: ContentItem;

  items: ContentParagraphItemUnion[] = [];

  readonly key = `bible-content-paragraph-${uuid++}`;

  constructor(item: ContentItem) {
    this.item = item;
  }

  toString() {
    const firstItem = this.items.find((item) => item instanceof ContentItem) as
      | ContentItem
      | undefined;
    const lastItem = Array.from(this.items)
      .reverse()
      .find((item) => item instanceof ContentItem) as ContentItem | undefined;
    let reference = this.item.reference;
    if (firstItem && lastItem && firstItem !== lastItem) {
      reference = `${firstItem.reference} - ${lastItem.reference}`;
    }
    return `${this.constructor.name} (${reference})`;
  }
}

export class ContentVerse {
  readonly bookId: number;

  get chapterId() {
    return this.#itemPayload.chapter_id;
  }

  get chapterNumber() {
    return this.#itemPayload.chapter_number;
  }

  get hasRelatedItems() {
    return this.#versePayload.has_related_items;
  }

  get id() {
    return this.#versePayload.id;
  }

  #itemPayload: ContentItemPayload;

  readonly key = `bible-content-verse-${uuid++}`;

  get number() {
    return this.#itemPayload.verse_number;
  }

  get reference() {
    return this.#itemPayload.string;
  }

  get string() {
    const { verse_number, verse_number_end } = this.#itemPayload;
    if (verse_number_end && verse_number_end !== verse_number) {
      return `${verse_number}-${verse_number_end}`;
    } else {
      return `${verse_number}`;
    }
  }

  #versePayload: VersePayload;

  constructor(
    itemPayload: ContentItemPayload,
    versePayload: VersePayload,
    book_id: number,
  ) {
    this.bookId = book_id;
    this.#itemPayload = itemPayload;
    this.#versePayload = versePayload;
  }

  toString() {
    return `${this.constructor.name}`;
  }
}

export interface ContentVerseImagePayload {
  alt?: string;
  aspect_ratio?: number;
  type?: string;
  url: string;
}

export class ContentVerseImage {
  readonly key = `bible-content-image-${uuid++}`;

  readonly payload: ContentVerseImagePayload;

  constructor(payload: ContentVerseImagePayload) {
    this.payload = payload;
  }

  toString() {
    return `${this.constructor.name}`;
  }
}

export interface ImagesPayload {
  large: Nullable<string>;
  medium: Nullable<string>;
  mini: Nullable<string>;
  small: Nullable<string>;
}

export interface MovementReading {
  content_selection_query: string;
  id: number;
  images?: ImagesPayload;
  label?: string;
  number?: number;
  studies?: MovementReadingStudyPayload[];
  title: string;
  units?: MovementReadingUnitPayload[];
  [unknown: string]: unknown;
}

export interface MovementReadingStudyPayload {
  link_ids: number[];
  pattern_type: string;
  study: StudyPayload;
  [unknown: string]: unknown;
}

export interface MovementReadingUnitPayload {
  content_selection_query: string;
  first_verse_id: Nullable<number>;
  id: number;
  label?: string;
  title: string;
  [unknown: string]: unknown;
}

export interface QueryPayload {
  book_id: number;
  book_name: string;
  items?: ContentItemPayload[];
  query: string;
  translation_id: number;
  verses?: VersePayload[];
  [unknown: string]: unknown;
}

export interface RelatedResource {
  [unknown: string]: unknown;
}

export interface StudyPayload {
  id: number;
  [unknown: string]: unknown;
}

export interface TranslationPayload {
  category_name: string;
  copyright?: {
    citation?: Nullable<string>;
    description?: Nullable<string>;
    link?: Nullable<string>;
    logo?: Nullable<string>;
  };
  full_name: string;
  id: number;
  name: string;
  [unknown: string]: unknown;
}

export interface VersePayload {
  chapter_id: number;
  has_related_items: boolean;
  id: number;
  images_after_verse?: Nullable<ContentVerseImagePayload[]>;
  images_before_verse?: Nullable<ContentVerseImagePayload[]>;
  related_items?: RelatedResource[];
  verse_number: number;
  [unknown: string]: unknown;
}
